import { IPosition } from '../pages/positions/position';
import { WalletPlatform } from '../pages/wallets/types';
import { isSet } from '../helpers';

interface IPositionMessage {
  messageType: 'PositionStatus' | 'PositionOpen' | 'PositionClose' | 'PositionClosed' | 'PositionOpened';
  positionId: number;
}

interface IPositionStatus extends IPositionMessage {
  symbol: string;
  profitLoss?: string;
  margin?: string;
}

export interface IPositionOpen extends IPositionMessage {
  accountId: number;
  type: string;
  symbol: string;
  status: string;
  invest: number;
  openedAt: number;
  platform: string;
  size: number;
  side: string;
  closePositionAfterSeconds: number;
  openPrice: string;
  openingOrderExternalId: string;
}

export interface IPositionClose extends IPositionMessage {
  symbol: string;
  status: string;
  invest: number;
  closedAt: number;
  platform: string;
  size: number;
  side: string;
  closedPLInAccountCurrency: string;
  duration: number;
  openPrice: string;
  closePrice: string;
  profitLoss: string;
  closeReason: string;
}

const updatePositionFromPositionStatus = (position: IPosition, message: IPositionStatus): IPosition => {
  if (position.id !== message.positionId || !isSet(message.profitLoss)) {
    return position;
  }
  return {
    ...position,
    profitLoss: message.profitLoss,
  };
};

const updatePositionFromPositionClose = (position: IPosition, message: IPositionClose): IPosition => {
  if (position.id !== message.positionId) {
    return position;
  }

  // `platform` on the message is the client app — the value the API reports as `source`. Spreading
  // it would overwrite the trading platform with something like "wintrado", which is not one. Same
  // split as on position open below.
  const { messageType, platform, ...rest } = message;
  return {
    ...position,
    ...rest,
    source: platform ?? position.source,
  };
};

export const updatePositionsFromPositionStatus = (
  positions: Array<IPosition>,
  message: IPositionStatus
): Array<IPosition> => {
  return positions.map((position) => updatePositionFromPositionStatus(position, message));
};

export const updatePositionsFromPositionClose = (
  positions: Array<IPosition>,
  message: IPositionClose
): Array<IPosition> => {
  return positions.filter((position) => position.id !== message.positionId);
};

const updatePositionsFromPositionOpen = (positions: Array<IPosition>, message: IPositionOpen): Array<IPosition> => {
  const { messageType, positionId, ...newPosition } = message;
  return [
    {
      ...newPosition,
      id: positionId,
      // A gateway position's external id is its own id — that is what the backfill set, and the
      // live-edge WS only ever carries gateway positions.
      externalId: String(positionId),
      // The live-edge WS is Wintrado-only, and its `platform` field is the client app — the same
      // value the API now reports as `source`. Set both so a WS-inserted row matches a fetched one.
      platform: WalletPlatform.Wintrado,
      source: newPosition.platform,
      closeReason: null,
      closedPLInAccountCurrency: null,
      closedPL: null,
      closedAt: null,
      closePrice: null,
      profitLoss: null,
      amount: null,
      swap: null,
      takeProfit: null,
      stopLoss: null,
      commission: null,
      trailingStopLossPct: null,
      totalProfitLoss: null,
    },
    ...positions,
  ];
};

export const updatePositionsCacheFromWSMessage = (oldData: any, message: IPositionMessage) => {
  if (!isSet(oldData) || !isSet(oldData.positions)) {
    return oldData;
  }

  let positions = [];

  switch (message.messageType) {
    case 'PositionStatus':
      positions = updatePositionsFromPositionStatus(oldData.positions, message as IPositionStatus);
      break;
    case 'PositionClose':
      positions = updatePositionsFromPositionClose(oldData.positions, message as IPositionClose);
      break;
    case 'PositionOpen':
      positions = updatePositionsFromPositionOpen(oldData.positions, message as IPositionOpen);
      break;
    default:
      throw Error(`Invalid message type ${message.messageType}`);
  }

  return {
    ...oldData,
    positions,
  };
};

export const updatePositionCacheFromWSMessage = (oldData: IPosition, message: IPositionMessage) => {
  if (!isSet(oldData)) {
    return oldData;
  }

  switch (message.messageType) {
    case 'PositionStatus':
      return updatePositionFromPositionStatus(oldData, message as IPositionStatus);
    case 'PositionClose':
      return updatePositionFromPositionClose(oldData, message as IPositionClose);
    default:
      return oldData;
  }
};

/** Statuses where the float is still moving — same grouping the backend filters by. */
const LIVE_POSITION_STATUSES = ['new', 'open', 'in-closing'];

export const isLivePosition = (status: string): boolean => LIVE_POSITION_STATUSES.includes(status);

/**
 * The position's own P&L: the float while it is live, the realized figure once it is not.
 *
 * A closed MT5 position keeps the last float its stream reported, so preferring the float would
 * show a stale number there. Either can be missing, hence the fallback in both directions.
 */
export const tradePnL = (position: {
  status: string;
  profitLoss: string | number | null;
  closedPLInAccountCurrency: string | number | null;
}): string | number | null =>
  isLivePosition(position.status)
    ? position.profitLoss ?? position.closedPLInAccountCurrency
    : position.closedPLInAccountCurrency ?? position.profitLoss;
